import { useState } from "react";
import { Box, Button, Dialog, Flex, IconButton, Text } from "@radix-ui/themes";
import { Switch } from "@radix-ui/themes";
import {
  useSubscriptionUsers,
  useUpdateSubscriptionUser,
  useDeleteSubscriptionUser,
} from "@/hooks/useAnythinkPaySubscriptions";
import { useUsers } from "@/hooks/useCommonQueries";
import { LoadingScreen } from "@/components/ui/LoadingScreen";
import SelectField from "@/components/form/SelectField";
import { useAuth } from "@/providers/AuthProvider";
import { useConfirm } from "@/providers/ConfirmDialogProvider";
import { Form, Formik } from "formik";
import * as Yup from "yup";
import { Trash } from "lucide-react";
import { BasicTable, Column } from "@/components/ui/BasicTable";
import { SubscriptionUser } from "@/types/breeze/AnythinkPay";

interface SubscriptionUsersProps {
  subscriptionId: string;
}

const addUserSchema = Yup.object().shape({
  userId: Yup.number().required("User is required"),
  readonly: Yup.boolean().required(),
});

export default function SubscriptionUsers({
  subscriptionId,
}: SubscriptionUsersProps) {
  const { currentUser } = useAuth();
  const [isAddUserOpen, setIsAddUserOpen] = useState(false);
  const confirm = useConfirm();

  const { data: users, isLoading: isLoadingUsers } = useUsers();
  const { data: subscriptionUsers, isLoading: isLoadingSubscriptionUsers } =
    useSubscriptionUsers(subscriptionId);
  const updateSubscriptionUser = useUpdateSubscriptionUser();
  const deleteSubscriptionUser = useDeleteSubscriptionUser();

  if (isLoadingUsers || isLoadingSubscriptionUsers) {
    return <LoadingScreen fullPage={false} />;
  }

  const handleUpdateAccess = (userId: number, readonly: boolean) => {
    updateSubscriptionUser.mutate({
      subscriptionId,
      userId,
      readonly,
    });
  };

  const handleDeleteUser = async (userId: number) => {
    const confirmed = await confirm({
      title: "Remove User Access",
      message: "Are you sure you want to remove this user's access?",
      confirmText: "Remove",
      cancelText: "Cancel",
      variant: "danger",
    });

    if (confirmed) {
      deleteSubscriptionUser.mutate({
        subscriptionId,
        userId,
      });
    }
  };

  const availableUsers = users?.filter(
    (user) => !subscriptionUsers?.some((su) => su.user_id === user.id),
  );

  const columns: Column<SubscriptionUser>[] = [
    {
      header: "User",
      accessor: "user",
      cellRenderer: (row: SubscriptionUser) =>
        `${row.user.first_name} ${row.user.last_name}`,
    },
    {
      header: "Email",
      accessor: "user",
      cellRenderer: (row: SubscriptionUser) => row.user.email,
    },
    {
      header: "Readonly",
      accessor: "readonly",
      cellRenderer: (row: SubscriptionUser) =>
        currentUser?.is_tenant_administrator ? (
          <Switch
            checked={row.readonly}
            onCheckedChange={(checked) =>
              handleUpdateAccess(row.user_id, checked)
            }
            disabled={updateSubscriptionUser.isPending}
          />
        ) : (
          <Text>{row.readonly ? "Read-only" : "Full Access"}</Text>
        ),
    },
    ...(currentUser?.is_tenant_administrator
      ? [
          {
            header: "Actions",
            accessor: "user_id",
            cellRenderer: (row: SubscriptionUser) => (
              <IconButton
                color="red"
                variant="ghost"
                onClick={() => handleDeleteUser(row.user_id)}
                disabled={deleteSubscriptionUser.isPending}
              >
                <Trash />
              </IconButton>
            ),
          } as Column<SubscriptionUser>,
        ]
      : []),
  ];

  return (
    <Box>
      <Flex justify="between" align="center" mb="4">
        <Text size="4" weight="bold">
          User Access
        </Text>
        {currentUser?.is_tenant_administrator && (
          <Button onClick={() => setIsAddUserOpen(true)}>Add User</Button>
        )}
      </Flex>

      <BasicTable
        columns={columns}
        data={subscriptionUsers || []}
        isLoading={isLoadingSubscriptionUsers}
      />

      <Dialog.Root open={isAddUserOpen} onOpenChange={setIsAddUserOpen}>
        <Dialog.Content>
          <Dialog.Title>Add User Access</Dialog.Title>
          <Dialog.Description size="2" mb="4">
            Select a user and their access level for this subscription.
          </Dialog.Description>

          <Formik
            initialValues={{
              userId: 0,
              readonly: true,
            }}
            validationSchema={addUserSchema}
            onSubmit={(values) => {
              updateSubscriptionUser.mutate(
                {
                  subscriptionId,
                  userId: values.userId,
                  readonly: values.readonly,
                },
                {
                  onSuccess: () => {
                    setIsAddUserOpen(false);
                  },
                },
              );
            }}
          >
            {({ values }) => (
              <Form>
                <Flex direction="column" gap="3">
                  <SelectField
                    name="userId"
                    label="User"
                    required
                    type="number"
                  >
                    {availableUsers?.map((user) => (
                      <SelectField.Item key={user.id} value={user.id}>
                        {user.first_name} {user.last_name} ({user.email})
                      </SelectField.Item>
                    ))}
                  </SelectField>

                  <Box>
                    <Text as="div" size="2" mb="1" weight="bold">
                      Readonly
                    </Text>
                    <Switch
                      checked={values.readonly}
                      onCheckedChange={(checked) => (values.readonly = checked)}
                      name="readonly"
                    />
                  </Box>
                </Flex>

                <Flex gap="3" mt="4" justify="end">
                  <Dialog.Close>
                    <Button variant="outline" color="gray">
                      Cancel
                    </Button>
                  </Dialog.Close>
                  <Button
                    type="submit"
                    disabled={
                      !values.userId || updateSubscriptionUser.isPending
                    }
                  >
                    {updateSubscriptionUser.isPending
                      ? "Adding..."
                      : "Add User"}
                  </Button>
                </Flex>
              </Form>
            )}
          </Formik>
        </Dialog.Content>
      </Dialog.Root>
    </Box>
  );
}
